import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import FilmMark from "@/components/FilmMark";
import SocialShare from "@/components/SocialShare";
import RencontresFrieze from "@/components/RencontresFrieze";
import Comments from "@/components/Comments";
import VenteFlashChako from "@/components/VenteFlashChako";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function CarnetPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    let cancelled = false;
    const load = (attempt = 0) => {
      api.get(`/blog/${slug}`)
        .then((r) => { if (!cancelled) setPost(r.data); })
        .catch((e) => {
          if (cancelled) return;
          if (e?.response?.status === 404) setNotFound(true);
          else if (attempt < 3) setTimeout(() => load(attempt + 1), 1200 * (attempt + 1));
          else setNotFound(true);
        });
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-32 pb-24 px-6 md:px-12 max-w-3xl mx-auto text-center" data-testid="carnet-notfound">
        <div className="meta-label text-cinabre">§ Le Carnet · article introuvable</div>
        <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-tight">Cette page s'est <em className="italic">évaporée</em>.</h1>
        <p className="mt-6 font-serif italic text-xl text-fg/70">Comme une œuvre laissée trop longtemps sur un arbre, elle a peut-être été emportée par un promeneur.</p>
        <Link to="/carnet" className="btn-editorial mt-10 inline-flex">
          <ArrowLeft size={14} /> Retour au Carnet
        </Link>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="pt-32 pb-24 px-6 md:px-12 max-w-3xl mx-auto text-center" data-testid="carnet-loading">
        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 animate-pulse">Chargement de la chronique…</div>
      </main>
    );
  }

  return (
    <main className="pt-32 pb-24" data-testid="carnet-post">
      <FilmMark />

      <article className="px-6 md:px-12 max-w-[1200px] mx-auto">
        {/* Header */}
        <header className="max-w-3xl">
          <Link to="/carnet" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 hover:text-cinabre" data-testid="carnet-back">
            <ArrowLeft size={12} /> Le Carnet
          </Link>
          <div className="meta-label text-cinabre mt-8">§ {formatDate(post.published_at)} · {post.author}</div>
          <h1 className="mt-4 font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
            {post.title}
          </h1>
          {post.subtitle && (
            <p className="mt-6 font-serif italic text-2xl md:text-3xl leading-[1.35] text-fg/75">
              {post.subtitle}
            </p>
          )}
        </header>

        {/* Hero image */}
        {post.hero_image && (
          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mt-14 md:mt-20"
          >
            <div className="border border-fg/15 overflow-hidden bg-fg/5 aspect-[16/10]">
              <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover" />
            </div>
            {post.hero_caption && (
              <figcaption className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">
                {post.hero_caption}
              </figcaption>
            )}
          </motion.figure>
        )}

        {/* Body */}
        <div
          className="carnet-prose mt-16 md:mt-20 max-w-[720px]"
          dangerouslySetInnerHTML={{ __html: post.body_html }}
          data-testid="carnet-body"
        />

        {/* Rencontres du Fil — chronique visuelle collective (Fée du Canal article) */}
        {post.slug === "la-fee-du-canal" && <RencontresFrieze />}

        {/* Vente flash Chako Design — 3 jours (Ven 11 · Sam 12 · Dim 13 septembre 2026) */}
        {post.slug === "epicurienne-x-chako-design" && <VenteFlashChako />}

        {/* Share bar */}
        <div className="mt-16 max-w-[720px]">
          <SocialShare
            title={post.title}
            text={`${post.title} — Le Fil du Midi`}
            label="Partager cette chronique"
            shareUrl={`${window.location.origin}/api/og/carnet/${post.slug}`}
          />
        </div>

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="mt-16 pt-8 border-t border-fg/10 flex flex-wrap items-center gap-2 max-w-[720px]">
            <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 mr-2">§ Mots-clés</span>
            {post.tags.map((t) => (
              <span key={t} className="px-3 py-1 text-[13px] font-serif border border-fg/25 text-fg/75">
                {t}
              </span>
            ))}
          </div>
        )}

        {/* CTA back */}
        <div className="mt-16 flex items-center gap-6 border-t border-fg/10 pt-10">
          <Link to="/carnet" className="btn-editorial ghost">
            <ArrowLeft size={14} /> Toutes les chroniques
          </Link>
          <Link to="/rejoindre" className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre hover:underline">
            Devenir une Fée du Fil →
          </Link>
        </div>

        {/* Comments */}
        <Comments slug={post.slug} />
      </article>
    </main>
  );
}
